import React from 'react';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import AppDownloadButtons from './AppDownloadButtons';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import LogoCircleIcon from '../../../../shared/images/idnty-logo-circle.svg';

const CHECKMARK_COLOR = '#22C55E';

const Wrapper = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 480px;
  padding: 0 24px;
  width: 100%;
`;

const CheckmarkContainer = styled('div')`
  align-items: center;
  background: transparent;
  border: 4px solid ${CHECKMARK_COLOR};
  border-radius: 50%;
  display: flex;
  height: 80px;
  justify-content: center;
  width: 80px;
`;

const YoureInText = styled('h1')`
  color: #ffffff;
  font-family: 'Roboto Mono', 'Nunito Sans', sans-serif;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.5;
  margin: 0;
  text-align: center;
  text-transform: uppercase;
`;

const BodyText = styled('p')`
  color: ${(props) => props.textColor || '#ffffff'};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
  max-width: ${(props) => props.maxWidth || '600px'};
  text-align: center;
  width: 100%;
`;

const Divider = styled('div')`
  align-items: center;
  display: flex;
  gap: 12px;
  width: 100%;
`;

const DividerLine = styled('hr')`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  flex: 1;
  margin: 0;
`;

const DividerIcon = styled('img')`
  flex-shrink: 0;
  height: 19px;
  width: 19px;
`;

const DownloadSection = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

// Mutually exclusive personas that can land on this page - EXISTING_USER had
// a TeamBuildr account and just joined IDNTY; ALREADY_JOINED was in IDNTY
// before this visit.
export const USER_TYPE = {
  NEW_USER: 1,
  EXISTING_USER: 2,
  ALREADY_JOINED: 3,
};

const BODY_COPY_BY_USER_TYPE = {
  [USER_TYPE.NEW_USER]: (
    <>
      Your account has been successfully created!
      To get started, please download the
      {' '}
      <b>TeamBuildr Training</b>
      {' '}
      app and sign in using the Google account or email and password
      you just used to register.
    </>
  ),
  [USER_TYPE.EXISTING_USER]: (
    <>
      You have successfully joined IDNTY!
      To get started, download the TeamBuildr mobile app and login
      with the credentials you provided. Then use the
      {' '}
      <b>“Switch Organization”</b>
      {' '}
      option in the app sidebar to get your free programs!
    </>
  ),
  [USER_TYPE.ALREADY_JOINED]: (
    <>
      You&apos;re already part of IDNTY! Open the
      {' '}
      <b>TeamBuildr Training</b>
      {' '}
      app and sign in to continue your training journey.
    </>
  ),
};

const IdntyDownloadView = ({ showSuccessText, userType }) => (
  <Wrapper>
    <CheckmarkContainer>
      <IcomoonReact iconSet={iconSet} icon='checkmark' size={32} color={CHECKMARK_COLOR} />
    </CheckmarkContainer>
    <YoureInText>You&apos;re In!</YoureInText>
    {showSuccessText && (
      <BodyText maxWidth='600px' textColor='#ffffff'>
        {BODY_COPY_BY_USER_TYPE[userType]}
      </BodyText>
    )}
    <Divider>
      <DividerLine />
      <DividerIcon src={LogoCircleIcon} alt='' />
      <DividerLine />
    </Divider>
    <DownloadSection>
      <AppDownloadButtons />
    </DownloadSection>
  </Wrapper>
);

IdntyDownloadView.propTypes = {
  showSuccessText: PropTypes.bool,
  userType: PropTypes.oneOf(Object.values(USER_TYPE)),
};

IdntyDownloadView.defaultProps = {
  showSuccessText: true,
  userType: USER_TYPE.NEW_USER,
};

export default IdntyDownloadView;
